import { ActiveQuotesTab } from "@/components/dashboard/tabs/ActiveQuotesTab";
import { AcceptedQuotesTab } from "@/components/dashboard/tabs/AcceptedQuotesTab";
import { TradeExecutionsTab } from "@/components/dashboard/tabs/TradeExecutionsTab";
import { StatsCards } from "@/components/dashboard/tabs/components/StatsCards";

import { useState, useEffect } from "react";
import { quotesAPI, assetsAPI } from "@/services/api";

import { Header } from "@/components/layout/Header";
import { useToast } from "@/hooks/use-toast";
import { Asset } from "@/types/assets";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";

export function DashboardPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [assets, setAssets] = useState<Asset[]>([]);

  // Determine active tab from URL
  const tabFromPath = () => {
    if (location.pathname.endsWith("/accepted-quotes")) return "accepted-quotes";
    if (location.pathname.endsWith("/trades")) return "executed";
    // Default to active-quotes
    return "active-quotes";
  };

  const activeTab = tabFromPath();

  const handleTabChange = (tab: string) => {
    if (tab === "active-quotes") navigate("/active-quotes");
    else if (tab === "accepted-quotes") navigate("/accepted-quotes");
    else if (tab === "executed") navigate("/trades");
  };

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await assetsAPI.getAssets();
        setAssets(response);
      } catch (error) {
        console.error("Failed to fetch assets:", error);
      }
    };
    fetchAssets();
  }, []);

  const handleExportCSV = async () => {
    try {
      await quotesAPI.exportQuotesCSV();
    } catch (error) {
      console.error("Failed to export quotes:", error);
    }
  };

  const handleQuoteCreated = () => {
    // Switch to active quotes tab when a new quote is created
    if (activeTab !== "active-quotes") {
      handleTabChange("active-quotes");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />
      {/* Main Content */}
      <main className="container py-6">
        {/* Stats Cards Grid */}
        <StatsCards />

        {/* Custom Tab Navigation */}
        <div className="mt-6">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <button
              onClick={() => handleTabChange("active-quotes")}
              className={`
                px-6 py-4 rounded-lg text-lg font-medium transition-all
                ${
                  activeTab === "active-quotes"
                    ? "bg-white border-[3px] border-black hover:bg-slate-200 rounded-xl"
                    : "bg-slate-200 text-gray-700 hover:bg-slate-100"
                }
              `}
            >
              Active Quotes
            </button>
            <button
              onClick={() => handleTabChange("accepted-quotes")}
              className={`
                px-6 py-4 rounded-lg text-lg font-medium transition-all
                ${
                  activeTab === "accepted-quotes"
                    ? "bg-white border-[3px] border-black hover:bg-slate-200 rounded-xl"
                    : "bg-slate-200 text-gray-700 hover:bg-slate-100"
                }
              `}
            >
              Accepted Quotes
            </button>
            <button
              onClick={() => handleTabChange("executed")}
              className={`
                px-6 py-4 rounded-lg text-lg font-medium transition-all
                ${
                  activeTab === "executed"
                    ? "bg-white border-[3px] border-black hover:bg-slate-200 rounded-xl"
                    : "bg-slate-200 text-gray-700 hover:bg-slate-100"
                }
              `}
            >
              Executed Trades
            </button>
          </div>

          {/* Content */}
          <div className="mt-6">
            <Routes>
              <Route
                path="/active-quotes"
                element={
                  <ActiveQuotesTab
                    handleExportCSV={handleExportCSV}
                    onQuoteCreated={handleQuoteCreated}
                    assets={assets}
                  />
                }
              />
              <Route
                path="/accepted-quotes"
                element={<AcceptedQuotesTab handleExportCSV={handleExportCSV} />}
              />
              <Route
                path="/trades"
                element={<TradeExecutionsTab handleExportCSV={handleExportCSV} />}
              />
              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/active-quotes" replace />} />
            </Routes>
          </div>
        </div>
      </main>
    </div>
  );
}
